import { useThree, type ThreeEvent } from '@react-three/fiber'
import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import {
  localizarPontoNoMundo,
  pontoMaisPertoDoRaio,
  type AncoragemTabletop,
  type CordaTabletop,
  type ObjetoTabletop,
  type ReferenciaPonto,
  type VetorTabletop,
} from '../../dominio/tabletop'

/**
 * As alças da corda selecionada: uma esfera em cada ponto do percurso (pontas e
 * meio). Arrastar a alça leva aquele ponto para outro ponto de ancoragem: o
 * ponto mais perto do mouse acende e gruda; soltar fora de qualquer ponto
 * deixa a corda como estava.
 */

const RAIO_ALCA_M = 0.1
const RAIO_AREA_M = 0.2

interface Arrasto {
  readonly indice: number
  readonly pointerId: number
}

interface Candidato {
  readonly referencia: ReferenciaPonto
  readonly mundo: VetorTabletop
}

export function AlcasCorda3D({
  objetos,
  cordas,
  ancoragens,
  idCorda,
  aoReposicionar,
  aoAlterarManipulacao,
}: {
  objetos: readonly ObjetoTabletop[]
  cordas: readonly CordaTabletop[]
  ancoragens?: readonly AncoragemTabletop[]
  idCorda: string
  aoReposicionar: (idCorda: string, indice: number, referencia: ReferenciaPonto) => void
  aoAlterarManipulacao?: (ativa: boolean) => void
}) {
  const camera = useThree((e) => e.camera)
  const gl = useThree((e) => e.gl)
  const [arrasto, setArrasto] = useState<Arrasto | null>(null)
  const [candidato, setCandidato] = useState<Candidato | null>(null)
  const [ponteiro, setPonteiro] = useState<VetorTabletop | null>(null)
  const candidatoRef = useRef<Candidato | null>(null)
  const corda = cordas.find((c) => c.id === idCorda)

  useEffect(() => {
    if (!arrasto || !corda) return
    const cena = { versao: 2 as const, nome: '', objetos, cordas, ancoragens }
    const raio = new THREE.Raycaster()
    const ponto = (e: PointerEvent): { origem: VetorTabletop; direcao: VetorTabletop } => {
      const caixa = gl.domElement.getBoundingClientRect()
      raio.setFromCamera(new THREE.Vector2(((e.clientX - caixa.left) / caixa.width) * 2 - 1, -((e.clientY - caixa.top) / caixa.height) * 2 + 1), camera)
      const { origin: o, direction: d } = raio.ray
      return { origem: [o.x, o.y, o.z], direcao: [d.x, d.y, d.z] }
    }
    const aoMover = (e: PointerEvent) => {
      if (e.pointerId !== arrasto.pointerId) return
      const { origem, direcao } = ponto(e)
      // sem excluir nada: o próprio ponto de partida também é um destino válido (volta ao lugar)
      const achado = pontoMaisPertoDoRaio(cena, origem, direcao)
      candidatoRef.current = achado
      setCandidato(achado)
      // enquanto não há destino, a alça segue o mouse na profundidade em que estava
      const atual = localizarPontoNoMundo(cena, corda.percurso[arrasto.indice]!)
      const profundidade = atual ? Math.hypot(atual[0] - origem[0], atual[1] - origem[1], atual[2] - origem[2]) : 6
      setPonteiro([origem[0] + direcao[0] * profundidade, origem[1] + direcao[1] * profundidade, origem[2] + direcao[2] * profundidade])
    }
    const terminar = (cancelado: boolean) => {
      const destino = candidatoRef.current
      candidatoRef.current = null
      setArrasto(null)
      setCandidato(null)
      setPonteiro(null)
      aoAlterarManipulacao?.(false)
      const atual = corda.percurso[arrasto.indice]
      if (!cancelado && destino && atual && !(destino.referencia.idObjeto === atual.idObjeto && destino.referencia.idPonto === atual.idPonto)) {
        aoReposicionar(corda.id, arrasto.indice, destino.referencia)
      }
    }
    const aoSoltar = (e: PointerEvent) => {
      if (e.pointerId === arrasto.pointerId) terminar(false)
    }
    const aoCancelar = (e: PointerEvent) => {
      if (e.pointerId === arrasto.pointerId) terminar(true)
    }
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') terminar(true)
    }
    window.addEventListener('pointermove', aoMover)
    window.addEventListener('pointerup', aoSoltar)
    window.addEventListener('pointercancel', aoCancelar)
    window.addEventListener('keydown', aoTeclar)
    return () => {
      window.removeEventListener('pointermove', aoMover)
      window.removeEventListener('pointerup', aoSoltar)
      window.removeEventListener('pointercancel', aoCancelar)
      window.removeEventListener('keydown', aoTeclar)
    }
    // o arrasto vale pela cena do momento em que começou
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrasto])

  if (!corda) return null
  const cena = { versao: 2 as const, nome: '', objetos, cordas, ancoragens }

  const comecar = (indice: number) => (e: ThreeEvent<PointerEvent>) => {
    if (e.nativeEvent.button !== 0) return
    e.stopPropagation()
    aoAlterarManipulacao?.(true)
    setArrasto({ indice, pointerId: e.nativeEvent.pointerId })
  }

  return (
    <group>
      {corda.percurso.map((ref, indice) => {
        const mundo = localizarPontoNoMundo(cena, ref)
        if (!mundo) return null
        const arrastando = arrasto?.indice === indice
        const posicao = arrastando ? (candidato?.mundo ?? ponteiro ?? mundo) : mundo
        return (
          <group key={indice} position={posicao as [number, number, number]}>
            <mesh onPointerDown={comecar(indice)}>
              <sphereGeometry args={[RAIO_AREA_M, 12, 10]} />
              <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>
            <mesh renderOrder={20} raycast={() => null}>
              <sphereGeometry args={[RAIO_ALCA_M, 16, 12]} />
              <meshBasicMaterial color={arrastando ? '#facc15' : '#ffffff'} depthTest={false} transparent opacity={arrastando ? 1 : 0.85} />
            </mesh>
            <mesh renderOrder={19} raycast={() => null}>
              <sphereGeometry args={[RAIO_ALCA_M * 1.35, 16, 12]} />
              <meshBasicMaterial color="#0f172a" depthTest={false} transparent opacity={0.55} />
            </mesh>
          </group>
        )
      })}
      {arrasto && candidato && (
        <group position={candidato.mundo as [number, number, number]}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} renderOrder={21} raycast={() => null}>
            <ringGeometry args={[0.14, 0.22, 32]} />
            <meshBasicMaterial color="#facc15" depthTest={false} transparent opacity={0.95} side={THREE.DoubleSide} />
          </mesh>
        </group>
      )}
    </group>
  )
}
